import React from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import I18n from 'i18n'

const DAY = 24 * 3600 * 1000

const CoachLoadChart = ({
  metric1Data = [],
  metric2Data = [],
  metric1Label = '',
  metric2Label = '',
  metric1ChartType = 'line',
  metric2ChartType = 'line'
}) => {
  const isColumn = (t) => (t || '').toLowerCase() === 'column'

  // The entries normally have timestamps with exact hour so I am normalizing to midday
  // so same day sessions are grouped together.
  // The logic of the chart:
  // All rpe metrics are bar charts and stacked per day.
  // All the other metrics are line charts.
  const toMidday = (ms) => {
    const d = new Date(ms)
    d.setHours(12, 0, 0, 0)
    return d.getTime()
  }

  // Get the sessions by day
  const sessionsByDay = (data) => {
    const map = new Map()
    data.forEach((p) => {
      const dayX = toMidday(p.x)
      const entry = (p.entries && p.entries[0]) || {}
      const timeStr = entry.time || null

      let sortKey = p.x
      if (timeStr && /^\d{2}:\d{2}$/.test(timeStr)) {
        const [h, m] = timeStr.split(':').map((n) => parseInt(n, 10))
        const d = new Date(dayX)
        d.setHours(h, m, 0, 0)
        sortKey = d.getTime()
      }
      const item = {
        x: dayX,
        y: p.y,
        sessionTime: timeStr || Highcharts.dateFormat('%H:%M', sortKey),
        rawX: sortKey
      }
      if (!map.has(dayX)) map.set(dayX, [])
      map.get(dayX).push(item)
    })

    const result = {}
    let maxSessions = 0
    for (const [dayX, arr] of map.entries()) {
      arr.sort((a, b) => a.rawX - b.rawX)
      const total = arr.reduce((s, it) => s + (typeof it.y === 'number' ? it.y : 0), 0)
      result[dayX] = { sessions: arr, total }
      if (arr.length > maxSessions) maxSessions = arr.length
    }
    return { byDay: result, maxSessions }
  }

  // Build series for one metric
  // Returns an array of series (one for line, multiple for stacked columns)
  const buildTrainingsForMetric = ({
    data,
    label,
    chartType,
    baseColor,
    stackKey,
    yAxisIndex
  }) => {
    if (!isColumn(chartType)) {
      const lineData = (data || []).map((p, idx) => ({
        x: p.x,
        y: p.y,
        sessionTime: (p.entries && p.entries[0] && p.entries[0].time) || '—',
        isIndividualSession: true,
        dayTotal: p.y,
        sessionIndex: 1,
        totalSessions: 1,
        _idx: idx
      }))
      return [
        {
          name: label,
          type: 'line',
          data: lineData,
          yAxis: yAxisIndex,
          color: baseColor,
          zIndex: 10
        }
      ]
    }

    const { byDay, maxSessions } = sessionsByDay(data || [])
    const allDays = Object.keys(byDay)
      .map((k) => parseInt(k, 10))
      .sort((a, b) => a - b)

    const subSeries = []
    for (let sIdx = 0; sIdx < Math.max(maxSessions, 1); sIdx += 1) {
      const seriesData = allDays.map((dayX) => {
        const { sessions, total } = byDay[dayX]
        const s = sessions[sIdx]
        if (!s) return { x: dayX, y: null }
        return {
          x: dayX,
          y: s.y,
          sessionTime: s.sessionTime,
          isIndividualSession: true,
          dayTotal: total,
          sessionIndex: sIdx + 1,
          totalSessions: sessions.length
        }
      })

      // Color brightness if different for every training in the same day.
      let color = baseColor
      try {
        if (sIdx > 0) {
          color = Highcharts
            .color(baseColor)
            .brighten(Math.min(0.12 * sIdx, 0.36))
            .get()
        }
      } catch (e) {}

      subSeries.push({
        name: label,
        type: 'column',
        data: seriesData,
        yAxis: yAxisIndex,
        color,
        zIndex: 1,
        stack: stackKey,
        showInLegend: sIdx === 0,
        pointRange: DAY
      })
    }

    return subSeries
  }

  const m1IsColumn = isColumn(metric1ChartType)
  const m2IsColumn = isColumn(metric2ChartType)

  const yAxisIndex1 = 0
  const yAxisIndex2 = 1

  const seriesMetric1 = buildTrainingsForMetric({
    data: metric1Data,
    label: metric1Label,
    chartType: metric1ChartType,
    baseColor: '#1976d2',
    stackKey: 'metric1',
    yAxisIndex: yAxisIndex1
  })

  const seriesMetric2 = buildTrainingsForMetric({
    data: metric2Data,
    label: metric2Label,
    chartType: metric2ChartType,
    baseColor: '#d32f2f',
    stackKey: 'metric2',
    yAxisIndex: yAxisIndex2
  })

  const options = {
    chart: {
      type: 'line',
      zoomType: 'x',
      alignTicks: false
    },
    title: { text: `${metric1Label} & ${metric2Label}` },
    xAxis: {
      type: 'datetime',
      title: { text: I18n.t('components.dashboards.coach_individual.load.chart.date') },
      dateTimeLabelFormats: {
        day: '%d %b',
        week: '%d %b',
        month: '%b %Y'
      }
    },
    yAxis: [
      {
        title: { text: metric1Label || null },
        opposite: false,
        showEmpty: true,
        visible: true,
        min: m1IsColumn ? 0 : null
      },
      {
        title: { text: metric2Label || null },
        opposite: true,
        showEmpty: true,
        visible: true,
        min: m2IsColumn ? 0 : null
      }
    ],
    series: [
      ...seriesMetric1,
      ...seriesMetric2
    ],
    tooltip: {
      shared: false,
      useHTML: true,
      hideDelay: 0,
      distance: 30,
      formatter: function () {
        const point = this.point
        const date = Highcharts.dateFormat('%d %b %Y', point.x)
        let tooltip = `<b>${date}</b><br/>`

        tooltip += `<span style="color:${this.series.color}">${this.series.name}</span>: <b>${point.y}</b><br/>`

        if (point.isIndividualSession) {
          tooltip += `<small><i>${I18n.t('components.dashboards.coach_individual.load.chart.session_at')} ${point.sessionTime || '—'}</i></small><br/>`
          if (point.totalSessions > 1) {
            tooltip += `<small>${I18n.t('components.dashboards.coach_individual.load.chart.session_of', {
              sessionIndex: point.sessionIndex,
              totalSessions: point.totalSessions,
              dayTotal: point.dayTotal
            })}</small><br/>`
          } else {
            tooltip += `<small><i>${I18n.t('components.dashboards.coach_individual.load.chart.only_session')}</i></small><br/>`
          }
        }

        return tooltip
      }
    },
    plotOptions: {
      series: {
        animation: false,
        lineWidth: 2,
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 5,
          lineWidth: 1,
          lineColor: '#FFFFFF'
        },
        states: {
          hover: {
            lineWidth: 3,
            marker: {
              radius: 12,
              lineWidth: 2
            }
          },
          inactive: { opacity: 1 }
        },
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: 0,
        stickyTracking: false,
        findNearestPointBy: 'xy'
      },
      column: {
        stacking: 'normal',
        pointPadding: 0.15,
        groupPadding: 0.15,
        borderWidth: 1,
        borderColor: '#FFFFFF'
      }
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom'
    }
  }

  return (
    <div>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  )
}

export default CoachLoadChart
